// Racine : la session (connexion, invitation, réinitialisation), le routage par hash, l'état du jour,
// les actions d'écriture et les messages éphémères. Les écrans du module Santé sont ceux de la console
// Brain ; un membre sans ce module a un accueil plus simple (le brief et l'agenda).

import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';

import type { CorpsMarche, CorpsPesee, CorpsSeance, Etat, ItemAgenda, Moi, Reponse } from '@alfred/shared';
import { HorsLigne, NonConnecte, api } from './lib/api';
import { type ActionEnFile, appliquerAEtat, lireFile, mettreEnFile, nouvelIdAction, retirerDeFile, traiterFile, viderFile } from './lib/fileActions';
import { AValider } from './screens/AValider';
import { Alfred } from './screens/Alfred';
import { Agenda } from './screens/Agenda';
import { Accueil } from './screens/Accueil';
import { Brief } from './screens/Brief';
import { Bureau, CadreBureau, ongletDe, useBureau } from './screens/Bureau';
import { Cerveau, NoteEcran } from './screens/Cerveau';
import { Connexion, Invitation, Reinitialisation } from './screens/Compte';
import { CarteQuestionDuJour, Connaissance, QuestionDuJourEcran, ThemeEcran } from './screens/Entretien';
import { Assistant, JournalActions } from './screens/Assistant';
import { Connexions } from './screens/Connexions';
import { Diagnostic } from './screens/Diagnostic';
import { Installer } from './screens/Installer';
import { Notifications } from './screens/Notifications';
import { RappelRapide } from './screens/RappelRapide';
import { bandeauAttendu, lireSuivi } from './lib/installation';
import { effacerDonnees, horsLigneActif } from './lib/horsLigne';
import { estInstallee, lireAbonnement } from './lib/push';
import { MesDonnees } from './screens/MesDonnees';
import { ConsentementEcran, NoticeEcran } from './screens/Notice';
import { Documents } from './screens/Documents';
import { Mails } from './screens/Mails';
import { Memoire } from './screens/Memoire';
import { Oauth } from './screens/Oauth';
import { Notification } from './screens/Notification';
import { Pesee } from './screens/Pesee';
import { Portails } from './screens/Portails';
import { Quete } from './screens/Quete';
import { Reglages } from './screens/Reglages';
import { Statut } from './screens/Statut';

// La console des agents ne se charge qu'à l'ouverture : son terminal (xterm) pèse plus que le reste de l'app.
const Atelier = lazy(() => import('./atelier/Atelier').then((m) => ({ default: m.Atelier })));

export interface Toast {
  texte: string;
  erreur?: boolean;
  annuler?: () => void;
}

/**
 * Chaque action de santé est enregistrée dans le cœur, pas encore dans le cerveau : le message
 * qui suit propose « Annuler » tant que la recopie du soir n'est pas passée. Un rappel coché
 * s'annule aussi.
 */
export interface Actions {
  seance(c: CorpsSeance, succes: string): Promise<boolean>;
  marche(c: CorpsMarche, succes: string): Promise<boolean>;
  pesee(c: CorpsPesee, succes: string): Promise<boolean>;
  rappelFait(item: ItemAgenda, succes: string): Promise<boolean>;
  /** Le rappel en une phrase (lot 6.4) : la phrase de confirmation, ou null s'il attend le réseau (lot 6.5). */
  rappelPhrase(texte: string): Promise<string | null>;
  /** Les gestes qui attendent le réseau (lot 6.5) : un écran qui lit lui-même ses données les applique. */
  file: ActionEnFile[];
  annuler(id: string, succes: string): Promise<boolean>;
  repondre(id: string, accepte: boolean): Promise<boolean>;
  toast(t: Toast): void;
  recharger(): Promise<void>;
}

export interface PropsEcran {
  etat: Etat;
  actions: Actions;
}

function routeCourante(): string {
  return window.location.hash.replace(/^#/, '') || '/';
}

/** Où revenir après la connexion (une autorisation OAuth interrompue par l'écran de connexion). */
function ecrireApresConnexion(hash: string): void {
  try {
    sessionStorage.setItem('alfred.apres-connexion', hash);
  } catch {
    // Stockage indisponible (navigation privée) : on reviendra à l'accueil.
  }
}

function lireApresConnexion(): string {
  try {
    const h = sessionStorage.getItem('alfred.apres-connexion');
    sessionStorage.removeItem('alfred.apres-connexion');
    return h ?? '#/';
  } catch {
    return '#/';
  }
}

/**
 * Où aller après la connexion : une autorisation OAuth interrompue d'abord ; sinon, dans l'app tout juste installée
 * (sur iPhone, elle a sa propre connexion), la suite de l'installation guidée tant que les notifications n'y sont pas.
 */
async function apresConnexion(): Promise<string> {
  const h = lireApresConnexion();
  if (h !== '#/' || !estInstallee() || !bandeauAttendu(lireSuivi())) return h;
  return (await lireAbonnement().catch(() => ({ actif: true }))).actif ? h : '#/installer';
}

/** Le membre connecté a-t-il changé depuis la dernière fois, sur cet appareil ? (Et retient le nouveau.) */
function changerDeCompte(membreId: string): boolean {
  try {
    const avant = localStorage.getItem('alfred.membre');
    localStorage.setItem('alfred.membre', membreId);
    return avant !== null && avant !== membreId;
  } catch {
    return false;
  }
}

/** « 07:12 » aujourd'hui, sinon « 03/11 à 22:49 ». */
function dateDesDonnees(iso: string): string {
  const d = new Date(iso);
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? heure : `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} à ${heure}`;
}

/** Les routes ouvertes sans session : un lien reçu par SMS doit marcher dans n'importe quel navigateur. */
const PUBLIQUES = /^\/(connexion$|invitation\/|reinitialisation\/)/;

export function App() {
  const [route, setRoute] = useState(routeCourante);
  const [moi, setMoi] = useState<Moi | null | 'deconnecte'>(null);
  const [etat, setEtat] = useState<Etat | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  // Hors ligne (lot 6.3) : la date des données gardées, '' si inconnue ; null en ligne.
  const [horsLigne, setHorsLigne] = useState<string | null>(null);
  // Les gestes qui attendent le réseau (lot 6.5).
  const [file, setFile] = useState<ActionEnFile[]>([]);
  const envoiEnCours = useRef(false);
  const membreCourant = useRef<string | null>(null);
  const bureau = useBureau();
  const minuterie = useRef<number | undefined>(undefined);

  const afficher = useCallback((t: Toast) => {
    window.clearTimeout(minuterie.current);
    setToast(t);
    minuterie.current = window.setTimeout(() => setToast(null), t.annuler ? 6000 : 3500);
  }, []);

  /** Renvoie la file, dans l'ordre et avec les mêmes identifiants : le cœur ne refait rien de ce qu'il a déjà reçu. */
  const envoyerFile = useCallback(async () => {
    if (envoiEnCours.current) return;
    const enAttente = await lireFile();
    setFile(enAttente);
    if (!enAttente.length) return;
    envoiEnCours.current = true;
    try {
      const bilan = await traiterFile(enAttente, (a) => api.renvoyer(a.chemin, a.corps, a.id));
      for (const id of [...bilan.parties, ...bilan.refusees.map((r) => r.id)]) await retirerDeFile(id);
      setFile(await lireFile());
      const [refusee] = bilan.refusees;
      if (refusee) afficher({ texte: `Pas enregistré : ${refusee.libelle} (${refusee.raison}).`, erreur: true });
      else if (bilan.parties.length) afficher({ texte: bilan.parties.length > 1 ? `${bilan.parties.length} actions envoyées.` : 'Action envoyée.' });
    } finally {
      envoiEnCours.current = false;
    }
  }, [afficher]);

  const recharger = useCallback(async () => {
    if (PUBLIQUES.test(routeCourante())) return;
    try {
      const m = await api.moi();
      membreCourant.current = m.membre.id;
      // Un autre compte sur cet appareil : ce qui était gardé pour le hors-ligne, et sa file, ne sont pas à lui.
      if (changerDeCompte(m.membre.id)) await Promise.all([effacerDonnees().catch(() => undefined), viderFile()]);
      await envoyerFile();
      setMoi(m);
      if (m.modules.sante) setEtat(await api.etat());
      setErreur(null);
    } catch (e) {
      if (e instanceof NonConnecte) {
        setMoi('deconnecte');
        // Une autorisation OAuth en cours (lot 7.3) : on y revient après la connexion.
        if (routeCourante().startsWith('/oauth?')) ecrireApresConnexion(`#${routeCourante()}`);
        if (routeCourante() !== '/connexion') window.location.hash = '#/connexion';
      } else {
        setErreur((e as Error).message);
      }
    }
  }, []);

  useEffect(() => {
    const surHash = () => {
      setRoute(routeCourante());
      window.scrollTo(0, 0);
    };
    // L'app revient au premier plan (le lendemain, après une séance…) : l'état a pu changer.
    const surVisibilite = () => document.visibilityState === 'visible' && void recharger();
    window.addEventListener('hashchange', surHash);
    const surReseau = (e: Event) => setHorsLigne((e as CustomEvent<string | null>).detail);
    window.addEventListener('alfred:reseau', surReseau);
    // Le réseau revient : la file part sans attendre le prochain passage.
    const surRetour = () => void recharger();
    window.addEventListener('online', surRetour);
    document.addEventListener('visibilitychange', surVisibilite);
    void recharger();
    const intervalle = window.setInterval(() => void recharger(), 60_000);
    return () => {
      window.removeEventListener('hashchange', surHash);
      window.removeEventListener('alfred:reseau', surReseau);
      window.removeEventListener('online', surRetour);
      document.removeEventListener('visibilitychange', surVisibilite);
      window.clearInterval(intervalle);
    };
  }, [recharger]);

  const echec = useCallback((e: unknown) => {
    if (e instanceof NonConnecte) {
      window.location.hash = '#/connexion';
      return;
    }
    afficher({ texte: (e as Error).message, erreur: true });
  }, [afficher]);

  /** Le réseau a manqué : le geste attend dans la file, avec son identifiant. Faux si ce n'est pas possible (hors-ligne désactivé). */
  const differer = useCallback(async (a: Pick<ActionEnFile, 'id' | 'genre' | 'chemin' | 'corps' | 'libelle'>, succes: string): Promise<boolean> => {
    const membreId = membreCourant.current;
    if (!membreId || !(await horsLigneActif())) return false;
    try {
      await mettreEnFile({ ...a, membreId, creeLe: new Date().toISOString() });
    } catch {
      return false;
    }
    setFile(await lireFile());
    afficher({
      texte: `${succes} Partira au retour du réseau.`,
      annuler: () => void retirerDeFile(a.id).then(lireFile).then((f) => { setFile(f); afficher({ texte: 'Action annulée.' }); }),
    });
    return true;
  }, [afficher]);

  const ecrire = useCallback(
    async (appel: () => Promise<Reponse>, succes: string, annulable = true, differable?: Parameters<typeof differer>[0]): Promise<boolean> => {
      try {
        const r = await appel();
        setEtat(r.etat);
        const id = r.id;
        afficher({
          texte: succes,
          ...(annulable && id !== null
            ? { annuler: () => void api.annuler(id).then((x) => { setEtat(x.etat); afficher({ texte: 'Action annulée.' }); }).catch(echec) }
            : {}),
        });
        return true;
      } catch (e) {
        if (e instanceof HorsLigne && differable && (await differer(differable, succes))) return true;
        echec(e);
        return false;
      }
    },
    [afficher, differer, echec],
  );

  const actions: Actions = {
    file,
    // Une séance différée reste datée du jour où elle a été faite (`?date=`, comme la marche par son corps).
    seance: (c, s) => {
      const id = nouvelIdAction();
      return ecrire(() => api.seance(c, id), s, true, { id, genre: 'seance', chemin: `/api/sante/seance${etat ? `?date=${etat.date}` : ''}`, corps: c, libelle: s });
    },
    marche: (c, s) => {
      const id = nouvelIdAction();
      return ecrire(() => api.marche(c, id), s, true, { id, genre: 'marche', chemin: '/api/sante/marche', corps: c, libelle: s });
    },
    pesee: (c, s) => ecrire(() => api.pesee(c), s),
    rappelFait: async (item, s) => {
      if (!item.rappelId) return false;
      const id = nouvelIdAction();
      try {
        const r = await api.rappelFait(item.rappelId, item.jour, id);
        await recharger();
        afficher({ texte: s, annuler: () => void api.annulerRappel(r.occurrence).then(recharger).then(() => afficher({ texte: 'Action annulée.' })).catch(echec) });
        return true;
      } catch (e) {
        if (e instanceof HorsLigne && (await differer({ id, genre: 'rappel-fait', chemin: `/api/rappels/${item.rappelId}/fait`, corps: { jour: item.jour }, libelle: item.titre }, s))) return true;
        echec(e);
        return false;
      }
    },
    rappelPhrase: async (texte) => {
      const id = nouvelIdAction();
      // Écrite maintenant : « demain » reste le lendemain d'aujourd'hui, même si elle part plus tard.
      const le = new Date().toISOString();
      try {
        const r = await api.rappelRapide(texte, le, id);
        await recharger();
        return r.resume;
      } catch (e) {
        if (e instanceof HorsLigne && (await differer({ id, genre: 'rappel-phrase', chemin: '/api/rappels/rapide', corps: { texte, le }, libelle: texte }, 'Rappel gardé.'))) return null;
        throw e;
      }
    },
    annuler: (id, s) => ecrire(() => api.annuler(id), s, false),
    repondre: async (id, accepte) => {
      try {
        const d = await api.repondre(id, accepte);
        await recharger();
        // Une autorisation de l'assistant est exécutée à l'acceptation ; une proposition l'est plus tard, par Alfred.
        afficher({ texte: d.type === 'autorisation' ? (accepte ? 'C’est fait.' : 'Refusé : rien n’a été fait.') : accepte ? 'Proposition acceptée : Alfred l’appliquera.' : 'Proposition refusée.' });
        return true;
      } catch (e) {
        echec(e);
        return false;
      }
    },
    toast: afficher,
    recharger,
  };

  let ecran;
  let tableau = false;
  const invitation = /^\/invitation\/(.+)$/.exec(route);
  const reinitialisation = /^\/reinitialisation\/(.+)$/.exec(route);
  const note = /^\/cerveau\/(.+)$/.exec(route);
  const themeEntretien = /^\/entretien\/(\d)$/.exec(route);
  // Un nouveau compte passe par l'installation guidée (lot 6.1) ; la notice, si elle est requise, vient avant.
  if (invitation?.[1]) ecran = <Invitation code={invitation[1]} apres={() => { window.location.hash = '#/installer'; void recharger(); }} />;
  else if (reinitialisation?.[1]) ecran = <Reinitialisation code={reinitialisation[1]} />;
  else if (route === '/connexion' || moi === 'deconnecte') ecran = <Connexion apres={() => void apresConnexion().then((h) => { window.location.hash = h; void recharger(); })} />;
  else if (!moi) {
    ecran = (
      <main className="ecran centre">
        <p className="message muted">{erreur ? `Alfred est injoignable : ${erreur}` : 'Connexion…'}</p>
        {erreur && <button className="btn large" onClick={() => void recharger()}>Réessayer</button>}
      </main>
    );
  } else if (route === '/mes-donnees') {
    // Partir sans avoir accepté la notice reste possible : télécharger ses données, supprimer son compte.
    ecran = <MesDonnees actions={actions} />;
  } else if (moi.notice.manquante) {
    ecran = <ConsentementEcran actions={actions} apres={() => void recharger()} />;
  } else if (moi.modules.atelier && /^\/atelier(\/|$)/.test(route)) {
    ecran = (
      <Suspense fallback={<main className="ecran centre"><p className="message muted">Ouverture de l’Atelier…</p></main>}>
        <Atelier />
      </Suspense>
    );
  } else if (route === '/alfred') ecran = <Alfred moi={moi} actions={actions} />;
  else if (route.startsWith('/oauth?')) ecran = <Oauth params={new URLSearchParams(route.slice('/oauth?'.length))} actions={actions} />;
  else if (route === '/memoire') ecran = <Memoire actions={actions} />;
  else if (route === '/assistant') ecran = <Assistant moi={moi} actions={actions} />;
  else if (route === '/journal') ecran = <JournalActions actions={actions} />;
  else if (route === '/entretien') ecran = <Connaissance actions={actions} />;
  else if (route === '/question-du-jour') ecran = <QuestionDuJourEcran actions={actions} />;
  else if (themeEntretien?.[1]) ecran = <ThemeEcran key={themeEntretien[1]} numero={Number(themeEntretien[1])} actions={actions} />;
  else if (route === '/reglages') ecran = <Reglages moi={moi} actions={actions} />;
  else if (route === '/installer') ecran = <Installer actions={actions} />;
  else if (route === '/notifications') ecran = <Notifications actions={actions} />;
  else if (route === '/rappel-rapide') ecran = <RappelRapide actions={actions} />;
  else if (route === '/connexions') ecran = <Connexions actions={actions} />;
  else if (route === '/notice') ecran = <NoticeEcran />;
  else if (route === '/diagnostic') ecran = <Diagnostic actions={actions} />;
  else if (route === '/mails') ecran = <Mails actions={actions} />;
  else if (route === '/documents') ecran = <Documents actions={actions} />;
  else if (route === '/cerveau') ecran = <Cerveau sante={moi.modules.sante} />;
  else if (note?.[1]) ecran = <NoteEcran id={note[1]} actions={actions} />;
  else if (route === '/a-valider') ecran = <AValider actions={actions} sante={moi.modules.sante} />;
  else if (!moi.modules.sante) ecran = route === '/portails' ? <Agenda actions={actions} /> : <Accueil moi={moi} actions={actions} />;
  else if (!etat) ecran = <main className="ecran centre"><p className="message muted">Chargement…</p></main>;
  else {
    // L'écran voit les gestes en attente comme déjà faits (lot 6.5) : on ne les refait pas.
    const props = { etat: appliquerAEtat(etat, file), actions };
    tableau = bureau && ['/notification', '/quete', '/portails', '/statut', '/pesee'].every((r) => r !== route);
    ecran =
      route === '/notification' ? <Notification {...props} />
      : route === '/quete' ? <Quete {...props} />
      : route === '/portails' ? <Portails {...props} />
      : route === '/statut' ? <Statut {...props} />
      : route === '/pesee' ? <Pesee {...props} />
      // Sur grand écran, le brief devient un tableau de bord qui réunit quête, statut et portails.
      : tableau ? <Bureau {...props} moi={moi} questionDuJour={<CarteQuestionDuJour actions={actions} />} />
      : <Brief {...props} atelier={moi.modules.atelier} questionDuJour={<CarteQuestionDuJour actions={actions} />} />;
  }

  // Sur grand écran, les autres écrans d'un compte connecté prennent le cadre du bureau (en-tête, navigation à gauche,
  // à la place de la barre du bas). L'Atelier garde sa console plein écran ; le tableau de bord a déjà le cadre.
  if (bureau && moi && moi !== 'deconnecte' && !moi.notice.manquante && !tableau && route !== '/connexion' && !PUBLIQUES.test(route)
    && !(moi.modules.atelier && /^\/atelier(\/|$)/.test(route))) {
    ecran = (
      <CadreBureau moi={moi} etat={etat && appliquerAEtat(etat, file)} actif={ongletDe(route)}>
        <div className="bureau-contenu">{ecran}</div>
      </CadreBureau>
    );
  }

  return (
    <>
      {(horsLigne !== null || file.length > 0) && (
        <p className="bandeau-hors-ligne" role="status">
          {[
            ...(horsLigne !== null ? [`Hors ligne${horsLigne ? ` · données de ${dateDesDonnees(horsLigne)}` : ''}`] : []),
            ...(file.length ? [file.length > 1 ? `${file.length} actions en attente` : '1 action en attente'] : []),
          ].join(' · ')}
        </p>
      )}
      {ecran}
      {toast && (
        <div className={`toast${toast.erreur ? ' erreur' : ''}`} role="status">
          <span>{toast.texte}</span>
          {toast.annuler && (
            <button className="btn petit" onClick={() => { toast.annuler?.(); setToast(null); }}>Annuler</button>
          )}
        </div>
      )}
    </>
  );
}
